import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import customerService from "./customerService";

/* ===============================
   THUNKS
=================================*/

// GET USERS
export const getUsers = createAsyncThunk(
  "customer/get-customers",
  async (_, thunkAPI) => {
    try {
      return await customerService.getUsers();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// CREATE LEAD
export const createLead = createAsyncThunk(
  "customer/create-lead",
  async (leadData, thunkAPI) => {
    try {
      return await customerService.createLead(leadData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// GET ALL LEADS
export const getLeads = createAsyncThunk(
  "customer/get-leads",
  async (_, thunkAPI) => {
    try {
      return await customerService.getLeads();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

// GET SINGLE LEAD
export const getLeadById = createAsyncThunk(
  "customer/get-lead",
  async (id, thunkAPI) => {
    try {
      return await customerService.getLeadById(id);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const updateLeadStatus = createAsyncThunk(
  "customer/update-lead-status",
  async (data, thunkAPI) => {
    try {
      return await customerService.updateLeadStatus(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* ================= FACEBOOK LEADS ================= */

export const syncFacebookFormLeads = createAsyncThunk(
  "customer/sync-facebook-form-leads",
  async (formId, thunkAPI) => {
    try {
      return await customerService.syncFacebookFormLeads(formId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const fetchFacebookFormLeads = createAsyncThunk(
  "customer/fetch-facebook-form-leads",
  async (formId, thunkAPI) => {
    try {
      return await customerService.fetchFacebookFormLeads(formId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* ================= CAMPAIGNS ================= */

export const getActiveCampaigns = createAsyncThunk(
  "customer/get-active-campaigns",
  async (_, thunkAPI) => {
    try {
      return await customerService.getActiveCampaigns();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getCampaignInsights = createAsyncThunk(
  "customer/get-campaign-insights",
  async (_, thunkAPI) => {
    try {
      return await customerService.getCampaignInsights();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* ================= WHATSAPP ================= */

export const getWhatsappChats = createAsyncThunk(
  "customer/get-whatsapp-chats",
  async (_, thunkAPI) => {
    try {
      return await customerService.getWhatsappChats();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getAgentWhatsappChats = createAsyncThunk(
  "customer/get-agent-whatsapp-chats",
  async (_, thunkAPI) => {
    try {
      const token = thunkAPI.getState().auth.user.token;
      return await customerService.getAgentWhatsappChats(token);
    } catch (error) {
      return thunkAPI.rejectWithValue(
        error?.response?.data || error.message
      );
    }
  }
);

export const getWhatsappMessagesByPhone = createAsyncThunk(
  "customer/get-whatsapp-messages-by-phone",
  async (phone, thunkAPI) => {
    try {
      return await customerService.getWhatsappMessagesByPhone(phone);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendWhatsappMessageFromCRM = createAsyncThunk(
  "customer/send-whatsapp-message-from-crm",
  async (messageData, thunkAPI) => {
    try {
      return await customerService.sendWhatsappMessageFromCRM(messageData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const sendWhatsappImageFromCRM = createAsyncThunk(
  "customer/send-whatsapp-image-from-crm",
  async (imageData, thunkAPI) => {
    try {
      return await customerService.sendWhatsappImageFromCRM(imageData);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getFacebookLeadForms = createAsyncThunk(
  "customer/get-facebook-lead-forms",
  async (_, thunkAPI) => {
    try {
      return await customerService.getFacebookLeadForms();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getSingleFacebookLeadForm = createAsyncThunk(
  "customer/get-single-facebook-lead-form",
  async (formId, thunkAPI) => {
    try {
      return await customerService.getSingleFacebookLeadForm(formId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

export const getFacebookFormLeads = createAsyncThunk(
  "customer/get-facebook-form-leads",
  async (formId, thunkAPI) => {
    try {
      return await customerService.getFacebookFormLeads(formId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  }
);

/* ===============================
   INITIAL STATE
=================================*/

const initialState = {
  customers: [],
  leads: [],
  singleLead: null,

  activeCampaigns: [],
  campaignInsights: [],
  activeCampaignsLoading: false,
  campaignInsightsLoading: false,

  whatsappChats: [],
  whatsappMessages: [],
  whatsappSendResult: null,
  whatsappImageSendResult: null,
  forms: [],
  selectedForm: null,
  formLeads: [],
  facebookFetchedLeads: [],
  facebookSyncResult: null,
  isError: false,
  isLoading: true,
  isSuccess: false,
  message: "",
};

/* ===============================
   SLICE
=================================*/

export const customerSlice = createSlice({
  name: "customer",
  initialState,
  reducers: {
    resetState: (state) => {
      state.isError = false;
      state.isLoading = false;
      state.isSuccess = false;
      state.message = "";
      state.facebookImportResult = null;
      state.facebookWebhookResult = null;
      state.activeCampaignsLoading = false;
      state.campaignInsightsLoading = false;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getUsers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getUsers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.customers = action.payload;
      })
      .addCase(getUsers.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(createLead.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createLead.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.leads.unshift(action.payload.lead);
      })
      .addCase(createLead.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(updateLeadStatus.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updateLeadStatus.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.leads = state.leads.map((lead) =>
          lead._id === action.payload._id ? action.payload : lead
        );
      })
      .addCase(updateLeadStatus.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getLeads.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.leads = action.payload.leads;
      })
      .addCase(getLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getLeadById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getLeadById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.singleLead = action.payload;
      })
      .addCase(getLeadById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getFacebookLeadForms.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFacebookLeadForms.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.forms = action.payload.forms || [];
        state.message =
          action.payload.message || "Facebook forms fetched successfully";
      })
      .addCase(getFacebookLeadForms.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message =
          action.payload?.error || action.payload?.message || action.payload;
      })

      .addCase(getSingleFacebookLeadForm.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getSingleFacebookLeadForm.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.selectedForm = action.payload.form || null;
        state.formLeads = action.payload.leads || [];
        state.message =
          action.payload.message || "Facebook form fetched successfully";
      })
      .addCase(getSingleFacebookLeadForm.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message =
          action.payload?.error || action.payload?.message || action.payload;
      })

      .addCase(getFacebookFormLeads.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getFacebookFormLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.isError = false;
        state.formLeads = action.payload.leads || [];
        state.message =
          action.payload.message || "Facebook form leads fetched successfully";
      })
      .addCase(getFacebookFormLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.isSuccess = false;
        state.isError = true;
        state.message =
          action.payload?.error || action.payload?.message || action.payload;
      })

      .addCase(fetchFacebookFormLeads.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(fetchFacebookFormLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.facebookFetchedLeads = action.payload?.data || action.payload || [];
      })
      .addCase(fetchFacebookFormLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(syncFacebookFormLeads.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(syncFacebookFormLeads.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.facebookSyncResult = action.payload;
      })
      .addCase(syncFacebookFormLeads.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getActiveCampaigns.pending, (state) => {
        state.isLoading = true;
        state.activeCampaignsLoading = true;
      })
      .addCase(getActiveCampaigns.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.activeCampaignsLoading = false;
        state.activeCampaigns = action.payload;
      })
      .addCase(getActiveCampaigns.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.activeCampaignsLoading = false;
        state.message = action.payload;
      })

      .addCase(getCampaignInsights.pending, (state) => {
        state.isLoading = true;
        state.campaignInsightsLoading = true;
      })
      .addCase(getCampaignInsights.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.campaignInsightsLoading = false;
        state.campaignInsights = action.payload;
      })
      .addCase(getCampaignInsights.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.campaignInsightsLoading = false;
        state.message = action.payload;
      })

      .addCase(getAgentWhatsappChats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAgentWhatsappChats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.whatsappChats = action.payload;
      })
      .addCase(getAgentWhatsappChats.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getWhatsappChats.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWhatsappChats.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.whatsappChats = action.payload;
      })
      .addCase(getWhatsappChats.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(getWhatsappMessagesByPhone.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getWhatsappMessagesByPhone.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.whatsappMessages = action.payload;
      })
      .addCase(getWhatsappMessagesByPhone.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(sendWhatsappMessageFromCRM.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendWhatsappMessageFromCRM.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.whatsappSendResult = action.payload;
      })
      .addCase(sendWhatsappMessageFromCRM.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })

      .addCase(sendWhatsappImageFromCRM.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(sendWhatsappImageFromCRM.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.whatsappImageSendResult = action.payload;
      })
      .addCase(sendWhatsappImageFromCRM.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { resetState } = customerSlice.actions;
export default customerSlice.reducer;